import type { UserProfile } from '../../types/userProfile';

import { EMPTY_DRAFT, type OnboardingDraft } from './types';

/** Build onboarding draft shape from DB row — safe when `profile` is null (legacy users). */
export function draftFromProfileRow(p: UserProfile | null): OnboardingDraft {
  if (!p) return { ...EMPTY_DRAFT };
  return {
    displayName:          p.displayName ?? '',
    headline:             p.headline ?? '',
    currentRole:          p.currentRole ?? '',
    experienceYears:      p.experienceYears != null ? String(p.experienceYears) : '',
    skills:               Array.isArray(p.skills) ? [...p.skills] : [],
    industry:             p.industry,
    jobSearchStatus:      p.jobSearchStatus,
    preferredJobTypes:    Array.isArray(p.preferredJobTypes) ? [...p.preferredJobTypes] : [],
    location:             p.location ?? '',
    salaryExpectationInr: p.salaryExpectationInr != null ? String(p.salaryExpectationInr) : '',
    linkedinUrl:          p.linkedinUrl ?? '',
    githubUrl:            p.githubUrl ?? '',
    portfolioUrl:         p.portfolioUrl ?? '',
    // Resume data
    professionalSummary:  p.professionalSummary ?? '',
    experiences:          Array.isArray(p.experiences) ? [...p.experiences] : [],
    education:            Array.isArray(p.education) ? [...p.education] : [],
    projects:             Array.isArray(p.projects) ? [...p.projects] : [],
    certifications:       Array.isArray(p.certifications) ? [...p.certifications] : [],
    languages:            Array.isArray(p.languages) ? [...p.languages] : [],
    resumeSource:         p.resumeSource,
    resumeFileUrl:        p.resumeFileUrl,
    resumeRawText:        p.resumeRawText,
    resumeUpdatedAt:      p.resumeUpdatedAt,
  };
}

/** Map draft + existing row into a canonical `UserProfile` for completeness UI (live preview while editing). */
export function mergedUserProfileForDisplay(
  userId: string,
  db: UserProfile | null,
  draft: OnboardingDraft,
): UserProfile {
  const expParsed = draft.experienceYears.trim()
    ? Number(draft.experienceYears)
    : null;
  const expYears = expParsed != null && Number.isFinite(expParsed) ? expParsed : null;

  const salaryParsed = draft.salaryExpectationInr.trim()
    ? Number(draft.salaryExpectationInr)
    : null;
  const salaryOk = salaryParsed != null && Number.isFinite(salaryParsed) ? salaryParsed : null;

  const iso = (): string =>
    db?.updatedAt ?? db?.createdAt ?? new Date().toISOString();

  return {
    id: db?.id ?? userId,
    displayName:          (draft.displayName.trim() || db?.displayName) ?? null,
    headline:             (draft.headline.trim() || db?.headline) ?? null,
    currentRole:          (draft.currentRole.trim() || db?.currentRole) ?? null,
    experienceYears:      expYears ?? db?.experienceYears ?? null,
    skills:               draft.skills,
    industry:             draft.industry ?? db?.industry ?? null,
    location:             (draft.location.trim() || db?.location) ?? null,
    jobSearchStatus:      draft.jobSearchStatus ?? db?.jobSearchStatus ?? null,
    preferredJobTypes:    draft.preferredJobTypes,
    salaryExpectationInr: salaryOk ?? db?.salaryExpectationInr ?? null,
    linkedinUrl:          (draft.linkedinUrl.trim() || db?.linkedinUrl) ?? null,
    githubUrl:            (draft.githubUrl.trim() || db?.githubUrl) ?? null,
    portfolioUrl:         (draft.portfolioUrl.trim() || db?.portfolioUrl) ?? null,
    // Resume data
    professionalSummary:  (draft.professionalSummary.trim() || db?.professionalSummary) ?? null,
    experiences:          draft.experiences.length > 0 ? draft.experiences : db?.experiences ?? [],
    education:            draft.education.length > 0 ? draft.education : db?.education ?? [],
    projects:             draft.projects.length > 0 ? draft.projects : db?.projects ?? [],
    certifications:       draft.certifications.length > 0 ? draft.certifications : db?.certifications ?? [],
    languages:            draft.languages.length > 0 ? draft.languages : db?.languages ?? [],
    resumeSource:         draft.resumeSource ?? db?.resumeSource ?? null,
    resumeFileUrl:        draft.resumeFileUrl ?? db?.resumeFileUrl ?? null,
    resumeRawText:        draft.resumeRawText ?? db?.resumeRawText ?? null,
    resumeUpdatedAt:      db?.resumeUpdatedAt ?? null,
    // App state
    onboardingCompleted:  db?.onboardingCompleted ?? false,
    blindMode:           db?.blindMode ?? false,
    tourCompleted:       db?.tourCompleted ?? false,
    lastReviewPromptAt:  db?.lastReviewPromptAt ?? null,
    createdAt:           db?.createdAt ?? iso(),
    updatedAt:           iso(),
  };
}

/** Produce a camelCase patch for `useUserProfile().save()` for one wizard/profile section. */
export function partialUserProfileFromDraftStep(
  step: number,
  draft: OnboardingDraft,
): Partial<UserProfile> {
  if (step === 1) {
    return {
      displayName:     draft.displayName.trim() || null,
      headline:        draft.headline.trim() || null,
      currentRole:     draft.currentRole.trim() || null,
      experienceYears: draft.experienceYears.trim()
        ? Number(draft.experienceYears)
        : null,
    };
  }
  if (step === 2) return { skills: draft.skills };
  if (step === 3) {
    return {
      industry:             draft.industry,
      jobSearchStatus:      draft.jobSearchStatus,
      preferredJobTypes:    draft.preferredJobTypes,
      location:             draft.location.trim() || null,
      salaryExpectationInr: draft.salaryExpectationInr.trim()
        ? Number(draft.salaryExpectationInr)
        : null,
    };
  }
  if (step === 4) {
    return {
      linkedinUrl:  draft.linkedinUrl.trim() || null,
      githubUrl:    draft.githubUrl.trim() || null,
      portfolioUrl: draft.portfolioUrl.trim() || null,
    };
  }
  // Step 5 — Resume data
  return {
    professionalSummary: draft.professionalSummary.trim() || null,
    experiences:         draft.experiences,
    education:           draft.education,
    projects:            draft.projects,
    certifications:      draft.certifications,
    languages:           draft.languages,
    resumeSource:        draft.resumeSource,
    resumeFileUrl:       draft.resumeFileUrl,
    resumeFileName:      draft.resumeFileName,
    resumeRawText:       draft.resumeRawText,
    resumeUpdatedAt:     new Date().toISOString(),
  };
}

/** Merge Preferences tab location-mode checkboxes into preferredJobTypes before save. */
export function mergeLocationModesIntoDraft(
  draft: OnboardingDraft,
  locationModes: string[],
): OnboardingDraft {
  const modeToType: Record<string, string> = {
    remote: 'remote',
    hybrid: 'hybrid',
    office: 'onsite',
  };
  const fromModes = locationModes
    .map((m) => modeToType[m])
    .filter((t): t is string => Boolean(t));
  if (fromModes.length === 0) return draft;
  return {
    ...draft,
    preferredJobTypes: [...new Set([...draft.preferredJobTypes, ...fromModes])],
  };
}
